import { useState } from 'react';
import { Filter, Inbox } from 'lucide-react';
import { useDraftsList } from '../../hooks/useApi';
import { DraftCard } from '../drafts/DraftCard';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorAlert } from '../common/ErrorAlert';
import { EmptyState } from '../common/EmptyState';
import type { DraftStatus, Platform } from '../../types';
import { PLATFORM_INFO, DRAFT_STATUS_INFO } from '../../types';

const FILTER_STATUSES: DraftStatus[] = ['draft', 'in_review', 'approved', 'scheduled', 'rejected', 'published'];

export function ApprovalQueue() {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [platformFilter, setPlatformFilter] = useState<string>('');

  const { data, loading, error, refetch } = useDraftsList({
    status: statusFilter || undefined,
    platform: platformFilter || undefined,
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Draft Review</h1>
          <p className="text-sm text-gray-500">
            Review, approve, and manage social media drafts
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Filter className="h-4 w-4 text-gray-400" />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Statuses</option>
          {FILTER_STATUSES.map((s) => (
            <option key={s} value={s}>
              {DRAFT_STATUS_INFO[s].label}
            </option>
          ))}
        </select>
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="input w-auto"
        >
          <option value="">All Platforms</option>
          {(Object.keys(PLATFORM_INFO) as Platform[]).map((p) => (
            <option key={p} value={p}>
              {(PLATFORM_INFO[p] || {icon:"📱",name:p}).icon} {(PLATFORM_INFO[p] || {icon:"📱",name:p}).name}
            </option>
          ))}
        </select>
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSpinner message="Loading drafts..." />
      ) : error ? (
        <ErrorAlert message={error} onRetry={refetch} />
      ) : !data?.items.length ? (
        <EmptyState
          icon={<Inbox className="h-12 w-12" />}
          title="No drafts found"
          description={
            statusFilter || platformFilter
              ? 'No drafts match your current filters. Try adjusting them.'
              : 'Drafts will appear here once AI generates them or you create them manually.'
          }
        />
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            {data.items.length} draft{data.items.length !== 1 ? 's' : ''}
          </p>
          {data.items.map((draft) => (
            <DraftCard
              key={draft.draftId}
              draft={draft}
              onAction={refetch}
              showContentInfo
            />
          ))}
        </div>
      )}
    </div>
  );
}
